
import { useState, ChangeEvent, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ImageUploadComponent from '../../components/ImageUploadComponent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditSpeciesToolbar from './EditSpeciesToolbar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import PublishStatusComponent from '../../components/PublishStatusComponent';
import EditBreed from './EditBreed';
import { OperationRequest } from '@peercolab/engine';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { SpeciesManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { useSpecies } from '../../providers/SpeciesProvider';
import { onRevert } from '../../utils/GeneralHelpers';
import { Published } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems';

interface EditSpeciesProps {
  id: string;
}

function EditSpecies({ id, onBack }: EditSpeciesProps & { onBack?: () => void }) {
  const { onError } = useToast();
  const { species: allSpecies, loadSpecies } = useSpecies();
  const [speciesName, setSpeciesName] = useState('');
  const [speciesTranslationKey, setSpeciesTranslationKey] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnail, setThumbnail] = useState<string>('');
  const [utilizesCommands, setUtilizesCommands] = useState(false);
  const [usesLitterBox, setUsesLitterBox] = useState(false);
  const [isIndoorOutdoorPet, setIsIndoorOutdoorPet] = useState(false);
  const [editingBreed, setEditingBreed] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newBreedName, setNewBreedName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [breedToDelete, setBreedToDelete] = useState<number | null>(null);
  const [breedSearch, setBreedSearch] = useState('');
  const [state, setState] = useState<Published>({ state: 'draft', isDirty: false });

  const species = allSpecies.find(s => s.id === id);

  const updateSpecies = async () => {
    (await loadSpecies(id)).maybeOk((species) => {
      setSpeciesName(species.name);
      setSpeciesTranslationKey(species.translationKey);
      setDescription(species.description);
      setThumbnail(species.thumbnail?.url ?? '');
      setUtilizesCommands(species.utilizesCommands);
      setUsesLitterBox(species.usesLitterBox);
      setIsIndoorOutdoorPet(species.isIndoorOutdoorPet);
      setState(species.state);
    })
  }

  useEffect(() => {
    updateSpecies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (!species)
    return null;

  const patchField = async <R,V>(operation: OperationRequest<unknown, R>, newValue: V, currentValue: V, revertHandler: (value: V) => void): Promise<void> => {
    if (newValue === currentValue) return
    const client = editingBreed ?
      Session.getBreedClient(id, editingBreed) :
      Session.getSpeciesClient(id);
    onError(onRevert((await client.request(operation)), () => revertHandler(currentValue)))
      .maybeOk(updateSpecies);
  }

  // Handlers for breed actions (move, edit, delete)
  const handleCsvUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      // Simple CSV: one breed name per line, skip empty lines, ignore header if present
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      let breedNames = lines;
      // If first line looks like a header, skip it
      if (breedNames[0]?.toLowerCase().includes('name')) {
        breedNames = breedNames.slice(1);
      }
    };
    reader.readAsText(file);
    // Reset input so same file can be uploaded again if needed
    e.target.value = '';
  };
  const handleMoveBreed = async (breedId: string, direction: 'up' | 'down') => {
    onError((await Session.getBreedClient(id, breedId).request(SpeciesManagement.moveBreed({ moveInstruction: { moveDirection: direction } })))
      .maybeOk(updateSpecies));
  };

  const handleDeleteBreed = (idx: number) => {
    setBreedToDelete(idx);
    setDeleteDialogOpen(true);
  };

  if (editingBreed) {
    return (
      <>
        <EditSpeciesToolbar
          species={species}
          breedId={editingBreed}
          onSpeciesClick={() => setEditingBreed(null)}
          onDataChanged={() => loadSpecies(id)}
        />
        <EditBreed
          species={species}
          breed={species.breeds.find(b => b.breed.id === editingBreed)!.breed}
          patchField={patchField}
          onBack={() => setEditingBreed(null)}
        />
      </>
    );
  }

  return (
    <Box>
      <EditSpeciesToolbar species={species} onBack={onBack} onDataChanged={() => loadSpecies(id)} />
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom>Species Details</Typography>
          <PublishStatusComponent
            status={state}
            onTransition={async (action) => {
              return (await Session.getSpeciesClient(id).request(SpeciesManagement.updateSpeciesState({ stateTransition: action })))
                .maybeOk(() => updateSpecies());
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={speciesName}
              onChange={e => setSpeciesName(e.target.value)}
              onBlur={() => patchField(SpeciesManagement.updateSpeciesName({name: speciesName}), speciesName, species.name, setSpeciesName)}
            />
            <TextField
              label="Translation Key"
              value={speciesTranslationKey}
              disabled
            />
            <TextField
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              multiline
              minRows={2}
              onBlur={() => patchField(SpeciesManagement.updateSpeciesDescription({description: description}), description, species.description, setDescription)}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <ImageUploadComponent value={thumbnail} onChange={async (file) => {
              onError((await Session.getSpeciesClient(id).request(SpeciesManagement.updateSpeciesThumbnail({image: file ? {
                filename: file.name,
                mimeType: file.mimeType,
                size: file.size,
                width: file.width,
                height: file.height,
                base64Content: file.base64Content,
              } : undefined})))
                .maybeOk(() => updateSpecies()));
            }} />
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={utilizesCommands}
              onChange={(e) => patchField(SpeciesManagement.updateSpeciesUtilizesCommands({utilizesCommands: e.target.checked}), e.target.checked, species.utilizesCommands, setUtilizesCommands)}
            />
          }
          label="Utilizes commands"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={usesLitterBox}
              onChange={(e) => patchField(SpeciesManagement.updateSpeciesUsesLitterBox({usesLitterBox: e.target.checked}), e.target.checked, species.usesLitterBox, setUsesLitterBox)}
            />
          }
          label="Uses litter box"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isIndoorOutdoorPet}
              onChange={(e) => patchField(SpeciesManagement.updateSpeciesIsIndoorOutdoorPet({isIndoorOutdoorPet: e.target.checked}), e.target.checked, species.isIndoorOutdoorPet, setIsIndoorOutdoorPet)}
            />
          }
          label="Is indoor/outdoor pet"
        />
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" gutterBottom>Breeds</Typography>
              <TextField
                size="small"
                placeholder="Search breeds"
                value={breedSearch}
                onChange={e => setBreedSearch(e.target.value)}
                sx={{ width: 200 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                component="label"
                sx={{ mr: 2 }}
              >
                Upload breeds from CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleCsvUpload}
                />
              </Button>
              <Button variant="contained" onClick={() => setAddDialogOpen(true)} sx={{ minWidth: 120 }}>Add Breed</Button>
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Published</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {species.breeds
                  .map(b => b.breed)
                  .filter(breed => breed.name.toLowerCase().includes(breedSearch.toLowerCase()))
                  .map((breed, idx) => (
                    <TableRow key={breed.name}>
                      <TableCell>{breed.name}</TableCell>
                      <TableCell>
                        <Checkbox checked={species.state.state === 'published'} disabled />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleMoveBreed(breed.id, 'up')} disabled={idx === 0}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleMoveBreed(breed.id, 'down')} disabled={idx === species.breeds.length - 1}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="primary" onClick={() => setEditingBreed(breed.id)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteBreed(idx)}>
                          <DeleteIcon />
                        </IconButton> 
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
            <DialogTitle>Add Breed</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Breed Name"
                fullWidth
                value={newBreedName}
                onChange={e => setNewBreedName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (newBreedName.trim()) {
                    onError((await Session.getSpeciesClient(id).request(SpeciesManagement.addBreed({ name: newBreedName.trim() })))
                      .maybeOk(updateSpecies));
                  }
                  setNewBreedName('');
                  setAddDialogOpen(false);
                }}
                variant="contained"
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Delete Breed</DialogTitle>
            <DialogContent>
              Are you sure you want to delete this breed?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (breedToDelete !== null) {
                    onError((await Session.getBreedClient(id, species.breeds[breedToDelete].breed.id).request(SpeciesManagement.removeBreed()))
                      .maybeOk(updateSpecies));
                  }
                  setDeleteDialogOpen(false);
                  setBreedToDelete(null);
                }}
                color="error"
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
}

export default EditSpecies;
